import React, { FC } from 'react';
import styled from 'styled-components';

import {
  OlmSelectivityCriterion,
  CombinatorialOperator,
  CombinatorialCollectionOperator,
  OperatorType,
} from '@/api/client';
import Text from '@/presentation/text';

interface ISelectivityCriterionInfoProps {
  criterion: OlmSelectivityCriterion;
  isLast?: boolean;
  isLastOfGroup?: boolean;
}

export const SSelectivityCriterionInfo = styled.div`
  background: ${({ theme }) => theme.colors.tertiaryBackground};
  border-radius: 10px;
  display: flex;
  justify-content: center;
  padding: 15px 10px;
  position: relative;
  flex-wrap: wrap;

  .sequence-value {
    font-family: 'notoSans-semiBold';
    font-weight: 700;
  }

  .relationship-value {
    text-transform: lowercase;
  }

  .operand {
    text-transform: lowercase;
    font-weight: 300;
  }

  ${Text} {
    text-align: center;
    white-space: break-spaces;
  }

  ${Text} + ${Text} {
    margin-left: 5px;
  }

  .combinatorial-pill {
    background-color: ${({ theme }) => theme.colors.primaryBackground};
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.colors.secondaryBorder};
    bottom: -18px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    font-size: 12px;
    position: absolute;
    text-align: center;
    width: 50px;
    z-index: 2;

    &.or {
      background-color: ${({ theme }) => theme.colors.secondaryBackground};
    }

    &.last {
      bottom: -32px;
    }
  }
`;

const SelectivityCriterionInfo: FC<ISelectivityCriterionInfoProps> = ({
  criterion,
  isLast = false,
  isLastOfGroup = false,
}) => {
  const isAndCombinatorial = criterion.nextCriterionRelationship === CombinatorialOperator.And;

  const renderValues = () => {
    const { values } = criterion;
    const joinChar =
      criterion.operand?.operator === OperatorType.Is_Between ||
      criterion.operand?.operator === OperatorType.Is_Not_Between
        ? ' and '
        : ', ';

    if (values) {
      if (criterion.field?.dataType?.controlType?.toUpperCase() !== 'LIST') {
        return values.map((value) => value.value).join(`${joinChar}`);
      } else {
        return values
          .map((value) => {
            if (value.value) {
              const desc = criterion.field?.values?.find((val) => {
                return val.code === value.value;
              })?.decode;
              if (desc) return desc;
            }
          })
          .join(`${joinChar}`);
      }
    }

    return '';
  };

  const renderNextCriterionRelationship = () => {
    return (
      <span className={`combinatorial-pill ${isAndCombinatorial ? '' : 'or'} ${isLastOfGroup ? 'last' : ''}`}>
        {isAndCombinatorial ? 'And' : 'Or'}
      </span>
    );
  };

  const renderValuesRelationship = () => {
    const { values, operand } = criterion;

    if (
      (values &&
        values.length > 1 &&
        operand?.operator !== OperatorType.Is_Between &&
        operand?.operator !== OperatorType.Is_Not_Between) ||
      (criterion.field?.isMultiEntry &&
        (criterion.operand?.operator === OperatorType.Is_Equal_To ||
          criterion.operand?.operator === OperatorType.Is_Not_Equal_To) &&
        criterion.operand?.name?.toUpperCase() !== 'IS BLANK' &&
        criterion.operand?.name?.toUpperCase() !== 'IS NOT BLANK')
    ) {
      const valuesRelationship = criterion.valuesRelationship === CombinatorialCollectionOperator.All ? 'all' : 'any';

      return <Text size="xxs">{valuesRelationship} of the following:</Text>;
    }

    return null;
  };

  const renderSequenceValue = () => {
    const extendedFieldSequence = criterion.extendedFieldSequence;
    if (extendedFieldSequence) {
      return (
        <Text size="xxs">
          with Sequence <span className="sequence-value">{extendedFieldSequence}</span>
        </Text>
      );
    } else {
      return null;
    }
  };

  return (
    <SSelectivityCriterionInfo>
      <Text size="xxs" weight="bold">
        {criterion.field?.name}
      </Text>
      {renderSequenceValue()}
      <Text size="xxs" className="operand">
        {criterion.operand?.name}
      </Text>
      {renderValuesRelationship()}
      <Text size="xxs" weight="bold">
        {renderValues()}
      </Text>
      {!isLast && renderNextCriterionRelationship()}
    </SSelectivityCriterionInfo>
  );
};

export default SelectivityCriterionInfo;
